/**
 * One watched platform site's inside view.
 *
 * Read again once a minute while the tab is visible, like the rest of the site
 * page: the numbers come straight from the platform, so an open page follows a
 * site being suspended or a domain being verified without a reload. A late
 * answer for an earlier read never overwrites a newer one.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getPlatformSite, type PlatformDetailResponse } from "../../../../lib/monitoringPlatform";
import { MONITOR_POLL_MS, useVisiblePoll } from "../useMonitors";

export function usePlatform(monitorId: string) {
  const [data, setData] = useState<PlatformDetailResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const rid = ++requestId.current;
    try {
      const res = await getPlatformSite(monitorId);
      if (!alive.current || rid !== requestId.current) return;
      setData(res);
      setFailed(false);
    } catch {
      if (!alive.current || rid !== requestId.current) return;
      setFailed(true);
    }
  }, [monitorId]);

  useEffect(() => {
    setData(null);
    setFailed(false);
    void load();
  }, [load]);

  useVisiblePoll(data !== null, MONITOR_POLL_MS, () => void load());

  return { data, failed, reload: load };
}
